import React, { useRef, useState } from 'react';
import { injectIntl } from 'react-intl';
import { connect } from 'react-redux';
import { useHistory } from 'react-router-dom';
import http from 'src/services/httpService';
import errorHandler from 'src/utils/error-handler';

import List from 'src/componentes/list/tree';
import ListColumn from 'src/componentes/list/list-column';
import { capitalizeFirstLetter } from 'src/utils/string';
import { ENTIDADES_CUSTOMIZADAS, FILTROS, COLUNAS } from 'src/utils/constants';
import Legenda from 'src/paginas/gestao-risco/legenda-farol-revisao';
import ValidarRestricaoNovo from 'src/componentes/validar-restricao-novo/ValidarRestricao';
import ContentManager from 'src/componentes/content-manager';
import LucideIcon from 'src/componentes/lucide-icon';
import { lowerCaseFirstLetter } from 'src/utils/string';
import Search from './search-drawer/search';
import SearchSchema from './search-schema';
import Novo from './novo-controle';
import StatusBadge from 'src/componentes/StatusBadge';

const PontoControleEdit = React.lazy(() => import('./edit'));

function PontoControleView({
  resources,
  intl,
  openLastFilter,
  filtroDefault,
  showToolbar,
  urlController = '/PontoControle',
  ...rest
}) {
  const history = useHistory();
  const savedCountRef = useRef(0);
  const [savedCount, setSavedCount] = useState(0);

  const handleCloseNew = (saved) => {
    if (saved) {
      savedCountRef.current += 1;
      setSavedCount(savedCountRef.current);
    }
  };

  const handleDeleteClick = async (item) => {
    try {
      const parametros = { id: item.id };
      const response = await http.post(
        '/PontoControle/ValidarRestricoes',
        parametros
      );
      const model = response.data;

      ContentManager.addContent(
        <ValidarRestricaoNovo
          title="excluirControle"
          url="/PontoControle/ValidarRestricoes"
          urlExclusao="/PontoControle/AtualizarStatusExcluido"
          item={item}
          onClose={handleCloseNew}
          labelCancelar="label.cancelar"
          labelAlterar="label.excluir"
          restricoesModel={model}
        />
      );
    } catch (error) {
      errorHandler(error);
    }
  };

  const renderFarol = (item) => {
    return (
      <span>
        {item.farol && item.farol.cor && (
          <LucideIcon
            icon={item.farol.icone}
            color={item.farol.cor}
            colorHover={item.farol.cor}
            size={22}
          />
        )}
      </span>
    );
  };

  const renderStatusUltimoCSA = (item) => {
    if (item?.statusCSA)
      return (
        <span className="text-primary">
          <StatusBadge
            text={item?.statusCSA?.nome}
            textColor={item.statusCSA?.cor}
            backgroundColor={item.statusCSA?.corFundo}
          />
        </span>
      );
  };

  const handleNewClick = (itemOrigem, tipoInclusao) => {
    ContentManager.addContent(
      <Novo
        url={urlController}
        tipoInclusaoParams={tipoInclusao}
        itemOrigem={itemOrigem}
        handleClose={handleCloseNew}
        afterSave={() => handleCloseNew(true)}
        history={history}
        urlEdit="/gerenciar/ponto-controle/edit"
      ></Novo>
    );
  };

  const handleEditClick = (item, parameters) => {
    ContentManager.addContent(
      <PontoControleEdit
        item={item}
        currenttabIndex={1}
        parameters={parameters}
        showDelete={true}
        onDeleting={handleDeleteClick}
        handleClose={handleCloseNew}
        afterSave={() => handleCloseNew(true)}
      />
    );
  };

  const renderPontoControle = (item, parameters) => {
    return (
      <a tabIndex="-1" onClick={() => handleEditClick(item, parameters)}>
        {item.descricao}
      </a>
    );
  };

  const createChildItems = [
    {
      label: intl.formatMessage(
        { id: 'labelCadastrarControleVinculado' },
        { pontodecontrole: lowerCaseFirstLetter(resources.pontodecontrole) }
      ),
      icon: 'List',
      parameters: { tipoId: 1 },
      visible: (item) => item.agrupador,
      onClick: (item) => handleNewClick(item, 1),
    },
    {
      label: capitalizeFirstLetter(
        intl.formatMessage(
          { id: 'labelCadastrarAgrupadorVincular' },
          { area: resources.area }
        )
      ),
      icon: 'Folder',
      parameters: { tipoId: 2 },
      visible: (item) => item.agrupador,
      onClick: (item) => handleNewClick(item, 2),
    },
  ];

  const funcaoPontoControle =
    rest.configuracao && rest.configuracao.funcaoPontoControle;
  const utilizarTipoPontoControle =
    rest.configuracao && rest.configuracao.utilizarTipoPontoControle;
  const frequenciaControle =
    rest.configuracao && rest.configuracao.frequenciaControle;
  const temPermissaoVisualizarCSA =
    rest.configuracao && rest.configuracao.temPermissaoVisualizarCSA;
  const configuracao = rest.configuracao;

  return (
    <React.Fragment>
      <List
        url="/PontoControle"
        afterSave={() => handleCloseNew(true)}
        showCustomFields
        tipoItem={ENTIDADES_CUSTOMIZADAS.PONTO_CONTROLE}
        editComponent={PontoControleEdit}
        onDeleting={handleDeleteClick}
        searchComponent={<Search configuracao={configuracao} />}
        searchSchema={SearchSchema}
        filterDrawer={true}
        showSearch
        handleNewClick={handleNewClick}
        tag={COLUNAS.PONTO_CONTROLE}
        filtroId={openLastFilter ? FILTROS.PONTO_CONTROLE : null}
        openLastFilter={openLastFilter}
        selectedFilter={{ valor: filtroDefault }}
        showToolBar={showToolbar}
        sort="Descricao asc"
        hierarchy={true}
        showPagination={true}
        retirarTreeCamposCustomizados={true}
        saved={savedCount}
        createChildItems={createChildItems}
        parameters={{ todosTipos: true }}
        {...rest}
      >
        <ListColumn
          headerText={intl.formatMessage({ id: 'descricao' })}
          valueFunction={renderPontoControle}
          tree={true}
          default
          valueField="descricao"
          required
          sortField="Descricao"
        />
        <ListColumn
          headerText={intl.formatMessage({ id: 'idIntegracao' })}
          default
          valueField="idIntegracao"
          sortField="IdIntegracao"
        />
        <ListColumn
          headerText={capitalizeFirstLetter(resources.responsaveis)}
          valueField="responsaveis"
        />
        <ListColumn
          headerText={capitalizeFirstLetter(
            intl.formatMessage({ id: 'periodicidade' })
          )}
          valueField="periodicidade"
          sortField="Periodicidade.Descricao"
        />
        <ListColumn
          headerText={capitalizeFirstLetter(
            intl.formatMessage(
              { id: 'labelTipoPontoDeControle' },
              { pontodecontrole: resources.pontodecontrole }
            )
          )}
          visible={utilizarTipoPontoControle}
          valueField="tipoPontoControle"
          sortField="TipoPontoControle.Descricao"
        />
        <ListColumn
          headerText={capitalizeFirstLetter(resources.farol)}
          valueField="farol"
          valueFunction={renderFarol}
        />
        <ListColumn
          headerText={capitalizeFirstLetter(
            intl.formatMessage({ id: 'tolerancia' })
          )}
          valueField="diasToleranciaRevisao"
          sortField="DiasToleranciaRevisao"
        />
        <ListColumn
          headerText={capitalizeFirstLetter(
            intl.formatMessage({ id: 'tipoTolerancia' })
          )}
          valueField="tipoTolerancia"
          sortField="TipoTolerancia.Descricao"
        />
        <ListColumn
          headerText={capitalizeFirstLetter(
            intl.formatMessage({ id: 'numeroRevisoesGeradasAutomaticamente' })
          )}
          valueField="numeroRevisoesGeradasAutomaticamente"
          sortField="NumeroRevisoesGeradasAutomaticamente"
        />
        <ListColumn
          visible={funcaoPontoControle}
          headerText={capitalizeFirstLetter(
            intl.formatMessage(
              { id: 'funcaoPontoControle' },
              { pontodecontrole: resources.pontodecontrole }
            )
          )}
          valueField="funcaoPontoControle"
          sortField="FuncaoPontoControle"
        />
        <ListColumn
          visible={frequenciaControle}
          headerText={capitalizeFirstLetter(
            intl.formatMessage({ id: 'label.frequencia' })
          )}
          valueField="frequenciaControle"
          sortField="FrequenciaControle"
        />
        <ListColumn
          headerText={capitalizeFirstLetter(resources.area)}
          valueField="area"
          sortField="Area"
        />
        <ListColumn
          visible={temPermissaoVisualizarCSA}
          headerText={capitalizeFirstLetter(
            intl.formatMessage({ id: 'statusCSA' })
          )}
          valueField="statusCSA"
          valueFunction={renderStatusUltimoCSA}
        />
      </List>
      <Legenda tipoFarol={1} />
    </React.Fragment>
  );
}

PontoControleView.defaultProps = {
  openLastFilter: true,
  showToolbar: true,
  showTitle: true,
};

function mapStateToProps(state) {
  return {
    resources: state.user.termos,
  };
}
export default injectIntl(connect(mapStateToProps)(PontoControleView));
